'use client';

import { c as _c } from "react/compiler-runtime";
import { observer } from 'mobx-react-lite';
import { AnimatePresence, LayoutGroup, motion, useReducedMotion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { Spinner } from './ui/spinner';
import { globalStore, MIN_LOADING_OVERLAY_DISPLAY_MS } from '@/app/globalStore';

/** Allow fade-in to settle before minimum hold timing (matches overlay show ease duration). */
const UNPINNED_ROW_FADE_IN_MS = 70;

/** Copy-only dwell: shorter than full overlay anti-flicker to keep status updates feeling active. */
const OVERLAY_COPY_DWELL_MS = 460;

/** Dimmed-backdrop blur (px) — keep moderate for smoother GPU compositing. */
const DIMMED_BACKDROP_BLUR_PX = 8;
const overlayEase = [0.22, 1, 0.36, 1] as const;
const feedItemTransition = {
  duration: 0.18,
  ease: overlayEase
};

/** Logo anchor stays near centre with a slight upward offset. */
const LOGO_TOP_OFFSET = '45%';
/** Space from logo bottom to first line of copy (`size-18` / 2 below anchor). */
const LOGO_TO_TEXT_GAP = '0.875rem';

/** Vertical gap between pinned headline, detail lines, and fallback (Tailwind `gap-2`). */
const OVERLAY_MESSAGE_STACK_GAP = 'gap-2';
/**
 * Top edge of the message block: logo anchor + half logo height + gap.
 * Use `top` (not `padding-top`): percentage padding is relative to **width**, which caused the huge gap.
 */
const MESSAGE_BLOCK_TOP = `calc(${LOGO_TOP_OFFSET} + 2.25rem + ${LOGO_TO_TEXT_GAP})`;
export type LoadingOverlayFeedItem = {
  /** Overlay registration id — stable across message text updates. */
  id: string;
  text: string;
};
type OverlayCopySnapshot = {
  pinnedTrimmed: string;
  pinnedKey: string | null;
  fallbackLine: string;
  primaryLineKey: string | null;
};
const EMPTY_OVERLAY_COPY: OverlayCopySnapshot = {
  pinnedTrimmed: '',
  pinnedKey: null,
  fallbackLine: '',
  primaryLineKey: null
};
function overlayCopyEmpty(s: OverlayCopySnapshot): boolean {
  return s.pinnedTrimmed.length === 0 && s.fallbackLine.length === 0;
}
function overlayCopyEqual(a: OverlayCopySnapshot, b: OverlayCopySnapshot): boolean {
  return a.pinnedTrimmed === b.pinnedTrimmed && a.pinnedKey === b.pinnedKey && a.primaryLineKey === b.primaryLineKey && a.fallbackLine === b.fallbackLine;
}

/**
 * Delays applying new copy so each line stays visible at least `minMs` (unless clearing or first paint).
 * Resets when the overlay is not visible so the next open does not inherit stale timing.
 */
function useOverlayCopyDwell(incoming: OverlayCopySnapshot, overlayVisible: boolean, minMs: number): OverlayCopySnapshot {
  const [displayed, setDisplayed] = useState<OverlayCopySnapshot>(() => incoming);
  const lastCommitAtRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const displayedRef = useRef(displayed);
  const incomingRef = useRef(incoming);
  displayedRef.current = displayed;
  incomingRef.current = incoming;
  const incomingSig = JSON.stringify(incoming);
  useEffect(() => {
    if (!overlayVisible) {
      setDisplayed(EMPTY_OVERLAY_COPY);
      lastCommitAtRef.current = 0;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    }
  }, [overlayVisible]);
  useEffect(() => {
    if (!overlayVisible) {
      return;
    }
    const next = incomingRef.current;
    if (overlayCopyEqual(next, displayedRef.current)) {
      return;
    }
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const now = Date.now();
    const goingToEmpty = overlayCopyEmpty(next);
    const priorEmpty = overlayCopyEmpty(displayedRef.current);
    const elapsed = now - lastCommitAtRef.current;
    const immediate = goingToEmpty || priorEmpty;
    const wait = immediate ? 0 : Math.max(0, minMs - elapsed);
    timerRef.current = setTimeout(() => {
      setDisplayed(incomingRef.current);
      lastCommitAtRef.current = Date.now();
      timerRef.current = null;
    }, wait);
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [incomingSig, overlayVisible, minMs]);
  return displayed;
}
type LoadingOverlayProps = {
  /** Single-line copy when no pinned headline (e.g. unpinned overlay stack). */
  message?: string;
  /** Pinned headline shown above `secondaryMessages` when non-empty after trim. */
  pinnedMessage?: string | null;
  /** Non-pinned active overlay lines in stack order (insertion order). */
  secondaryMessages?: readonly string[];
  /**
   * When set (e.g. from global store), used for stable keys and feed animations.
   * If omitted or empty, keys are derived from `secondaryMessages` indices.
   */
  secondaryFeed?: readonly LoadingOverlayFeedItem[];
  /** Overlay id for the pinned headline — for AnimatePresence keying. */
  pinnedKey?: string | null;
  /** Overlay id for the single-line fallback (`message`) when no pin and no secondary list. */
  primaryLineKey?: string | null;
  variant?: 'icon' | 'spinner';
  /** Cinematic dim, blur, and primary-tinted layers behind logo and copy. */
  useDimmedBackdrop?: boolean;
  /** When false, fades out and ignores pointer events — overlay stays mounted to avoid exit/enter flashes. */
  visible?: boolean;
  /**
   * When set, each id renders one unpinned queue row (sequential fade-up, minimum hold, then fade-out).
   * Used by the global overlay; local `LoadingOverlay` instances omit this.
   */
  unpinnedQueueRowIds?: readonly string[];
};
const LogoLoader = () => {
  const $ = _c(18);
  const reduceMotion = useReducedMotion();
  let t0;
  if ($[0] !== reduceMotion) {
    t0 = reduceMotion ? false : {
      opacity: 0,
      y: 8,
      scale: 0.96
    };
    $[0] = reduceMotion;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== reduceMotion) {
    t1 = reduceMotion ? undefined : {
      opacity: 1,
      y: 0,
      scale: 1
    };
    $[2] = reduceMotion;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== reduceMotion) {
    t2 = reduceMotion ? undefined : {
      duration: 0.2,
      ease: overlayEase
    };
    $[4] = reduceMotion;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== reduceMotion) {
    t3 = reduceMotion ? {
      opacity: [0.8, 1, 0.8]
    } : {
      scale: [1, 1.1, 1],
      opacity: [0.7, 1, 0.7]
    };
    $[6] = reduceMotion;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const t4 = reduceMotion ? 1.8 : 1.85;
  let t5;
  if ($[8] !== t4) {
    t5 = {
      duration: t4,
      repeat: Infinity,
      ease: "easeInOut"
    };
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t3 || $[11] !== t5) {
    t6 = <motion.img src="/.images/landing/Logo.png" alt="Loading" className="h-full w-full object-contain" animate={t3} transition={t5} />;
    $[10] = t3;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t0 || $[14] !== t1 || $[15] !== t2 || $[16] !== t6) {
    t7 = <motion.div className="relative size-18 drop-shadow-lg" initial={t0} animate={t1} transition={t2}>{t6}</motion.div>;
    $[13] = t0;
    $[14] = t1;
    $[15] = t2;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  return t7;
};
LogoLoader.displayName = 'LogoLoader';
const unpinnedRowExit = {
  duration: 0.2,
  ease: overlayEase
};

/**
 * One unpinned overlay id: shows queue head with fade-up, enforces minimum visible time, then exits before the next queued line.
 * Replace-mode updates head text in place (stable key) without re-fading or restarting the hold timer.
 * When the hold timer fires, the line is only hidden if another queued line follows or hide was requested.
 */
const UnpinnedQueueRow = observer(function UnpinnedQueueRow({
  rowId
}: {
  rowId: string;
}) {
  const queue = globalStore.loadingOverlayUnpinnedMessageQueues.get(rowId);
  const headEntry = queue?.[0];
  const [showBubble, setShowBubble] = useState(false);
  const exitingRef = useRef(false);
  const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fadeInDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearHoldTimer = () => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
  };
  const clearFadeDelay = () => {
    if (fadeInDelayRef.current) {
      clearTimeout(fadeInDelayRef.current);
      fadeInDelayRef.current = null;
    }
  };
  useEffect(() => {
    if (!headEntry) {
      setShowBubble(false);
      exitingRef.current = false;
      clearHoldTimer();
      clearFadeDelay();
      return;
    }
    if (!showBubble && !exitingRef.current) {
      setShowBubble(true);
    }
  }, [headEntry, headEntry?.key, showBubble]);
  useEffect(() => {
    if (!showBubble || !headEntry) {
      return;
    }
    clearFadeDelay();
    clearHoldTimer();
    fadeInDelayRef.current = setTimeout(() => {
      fadeInDelayRef.current = null;
      holdTimerRef.current = setTimeout(() => {
        holdTimerRef.current = null;
        const q = globalStore.loadingOverlayUnpinnedMessageQueues.get(rowId);
        const closeAfter = globalStore.loadingOverlayUnpinnedCloseAfterCurrent.get(rowId) === true;
        const hasNext = (q?.length ?? 0) > 1;
        if (hasNext || closeAfter) {
          exitingRef.current = true;
          setShowBubble(false);
        }
      }, MIN_LOADING_OVERLAY_DISPLAY_MS);
    }, UNPINNED_ROW_FADE_IN_MS);
    return () => {
      clearFadeDelay();
      clearHoldTimer();
    };
  }, [showBubble, headEntry, headEntry?.key, rowId]);
  const onExitComplete = () => {
    exitingRef.current = false;
    globalStore.completeUnpinnedOverlayHead(rowId);
  };
  if (!headEntry) {
    return null;
  }
  return <div className="flex min-h-6 w-full min-w-0 justify-center">
      <AnimatePresence mode="wait" onExitComplete={onExitComplete}>
        {showBubble ? <motion.div key={headEntry.key} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0,
        transition: {
          duration: 0.16,
          ease: overlayEase
        }
      }} exit={{
        opacity: 0,
        y: -8,
        transition: unpinnedRowExit
      }} className="w-full min-w-0 text-center">
            <p className={`
                w-full min-w-0 wrap-break-word text-[15px] font-normal leading-snug whitespace-pre-line
                text-active-foreground antialiased drop-shadow-sm
              `}>
              {headEntry.text}
            </p>
          </motion.div> : null}
      </AnimatePresence>
    </div>;
});
UnpinnedQueueRow.displayName = 'UnpinnedQueueRow';
const backdropCrossfade = {
  duration: 0.2,
  ease: overlayEase
};
const overlayShowHide = {
  duration: 0.16,
  ease: 'easeOut' as const
};
const overlayHide = {
  duration: 0.14,
  ease: 'easeIn' as const
};

/** Scroll area without visible scrollbar (content can still scroll on small viewports). */
const scrollNoBar = 'overflow-x-hidden overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden';
export const LoadingOverlay = t0 => {
  const $ = _c(84);
  const {
    message: t1,
    pinnedMessage: t2,
    secondaryMessages: t3,
    secondaryFeed,
    pinnedKey: t4,
    primaryLineKey: t5,
    variant: t6,
    useDimmedBackdrop: t7,
    visible: t8,
    unpinnedQueueRowIds
  } = t0;
  const message = t1 === undefined ? "" : t1;
  const pinnedMessage = t2 === undefined ? null : t2;
  const secondaryMessages = t3 === undefined ? [] : t3;
  const pinnedKey = t4 === undefined ? null : t4;
  const primaryLineKey = t5 === undefined ? null : t5;
  const variant = t6 === undefined ? "icon" : t6;
  const useDimmedBackdrop = t7 === undefined ? true : t7;
  const visible = t8 === undefined ? true : t8;
  let t9;
  if ($[0] !== pinnedMessage) {
    t9 = pinnedMessage?.trim() ?? "";
    $[0] = pinnedMessage;
    $[1] = t9;
  } else {
    t9 = $[1];
  }
  const pinnedTrimmed = t9;
  const secondaries = secondaryMessages.filter(_temp);
  const trimmedFeed = (secondaryFeed ?? []).map(_temp2).filter(_temp3);
  const resolvedFeed = trimmedFeed.length > 0 ? trimmedFeed : secondaries.map(_temp4);
  const hasPinnedIncoming = pinnedTrimmed.length > 0;
  const hasSecondaryIncoming = resolvedFeed.length > 0;
  let t10;
  if ($[2] !== hasPinnedIncoming || $[3] !== hasSecondaryIncoming || $[4] !== message) {
    t10 = !hasPinnedIncoming && !hasSecondaryIncoming ? message.trim() : "";
    $[2] = hasPinnedIncoming;
    $[3] = hasSecondaryIncoming;
    $[4] = message;
    $[5] = t10;
  } else {
    t10 = $[5];
  }
  const fallbackIncoming = t10;
  let t11;
  if ($[6] !== fallbackIncoming || $[7] !== pinnedKey || $[8] !== pinnedTrimmed || $[9] !== primaryLineKey) {
    t11 = {
      pinnedTrimmed,
      pinnedKey,
      fallbackLine: fallbackIncoming,
      primaryLineKey
    };
    $[6] = fallbackIncoming;
    $[7] = pinnedKey;
    $[8] = pinnedTrimmed;
    $[9] = primaryLineKey;
    $[10] = t11;
  } else {
    t11 = $[10];
  }
  const incomingCopy = t11;
  const copy = useOverlayCopyDwell(incomingCopy, visible, OVERLAY_COPY_DWELL_MS);
  const pinnedTrimmedDw = copy.pinnedTrimmed;
  const hasPinnedLine = pinnedTrimmedDw.length > 0;
  const resolvedFeedDw = resolvedFeed;
  const hasSecondaryLines = resolvedFeedDw.length > 0;
  const fallbackLine = copy.fallbackLine;
  const pinnedKeyDw = copy.pinnedKey;
  const primaryLineKeyDw = copy.primaryLineKey;
  const hasUnpinnedQueueRows = (unpinnedQueueRowIds?.length ?? 0) > 0;
  let t12;
  if ($[11] !== copy || $[12] !== hasSecondaryLines || $[13] !== hasUnpinnedQueueRows || $[14] !== incomingCopy) {
    t12 = hasUnpinnedQueueRows || hasSecondaryLines || !overlayCopyEmpty(incomingCopy) || !overlayCopyEmpty(copy);
    $[11] = copy;
    $[12] = hasSecondaryLines;
    $[13] = hasUnpinnedQueueRows;
    $[14] = incomingCopy;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  const hasVisibleMessage = t12;
  const showDimmedBackdropLayers = visible && useDimmedBackdrop;
  visible && !useDimmedBackdrop;
  let t13;
  if ($[16] !== hasVisibleMessage || $[17] !== visible) {
    t13 = visible && hasVisibleMessage ? {
      "aria-live": "polite" as const,
      role: "status" as const
    } : {};
    $[16] = hasVisibleMessage;
    $[17] = visible;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  const liveProps = t13;
  const t14 = motion;
  const t15 = false;
  const t16 = visible ? 1 : 0;
  const t17 = visible ? overlayShowHide : overlayHide;
  let t18;
  if ($[19] !== t16 || $[20] !== t17) {
    t18 = {
      opacity: t16,
      transition: t17
    };
    $[19] = t16;
    $[20] = t17;
    $[21] = t18;
  } else {
    t18 = $[21];
  }
  const t19 = !visible;
  const t20 = `
      fixed
      top-(--titlebar-height)
      right-0
      bottom-0
      left-0
      z-99999
      flex
      h-full
      min-h-0
      flex-col
      overflow-hidden
      px-6
      ${visible ? "pointer-events-auto cursor-wait" : "pointer-events-none"}
      `;
  const t21 = showDimmedBackdropLayers ? 1 : 0;
  const t22 = showDimmedBackdropLayers ? `blur(${DIMMED_BACKDROP_BLUR_PX}px)` : "blur(0px)";
  let t23;
  if ($[22] !== t21 || $[23] !== t22) {
    t23 = <motion.div className="absolute inset-0 bg-zinc-950/78" initial={false} animate={{
      opacity: t21,
      backdropFilter: t22,
      transition: backdropCrossfade
    }} />;
    $[22] = t21;
    $[23] = t22;
    $[24] = t23;
  } else {
    t23 = $[24];
  }
  const t24 = showDimmedBackdropLayers ? 1 : 0;
  let t25;
  if ($[25] !== t24) {
    t25 = <motion.div aria-hidden={true} className={"\n            pointer-events-none absolute inset-0\n            bg-[linear-gradient(to_top,color-mix(in_oklch,var(--color-primary)_2.25%,transparent)_0%,color-mix(in_oklch,var(--color-primary)_1.35%,transparent)_22%,transparent_48%)]\n          "} initial={false} animate={{
      opacity: t24,
      transition: backdropCrossfade
    }} />;
    $[25] = t24;
    $[26] = t25;
  } else {
    t25 = $[26];
  }
  const t26 = showDimmedBackdropLayers ? 1 : 0;
  let t27;
  if ($[27] !== t26) {
    t27 = <motion.div aria-hidden={true} className="pointer-events-none absolute inset-0 bg-linear-to-t from-zinc-950/50 via-zinc-950/12 to-transparent" initial={false} animate={{
      opacity: t26,
      transition: backdropCrossfade
    }} />;
    $[27] = t26;
    $[28] = t27;
  } else {
    t27 = $[28];
  }
  let t28;
  if ($[29] !== t23 || $[30] !== t25 || $[31] !== t27) {
    t28 = <div aria-hidden={true} className="absolute inset-0 overflow-hidden">{t23}{t25}{t27}</div>;
    $[29] = t23;
    $[30] = t25;
    $[31] = t27;
    $[32] = t28;
  } else {
    t28 = $[32];
  }
  const t29 = "relative z-10 flex min-h-0 w-full flex-1 flex-col items-center";
  let t30;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = {
      top: LOGO_TOP_OFFSET
    };
    $[33] = t30;
  } else {
    t30 = $[33];
  }
  const t31 = visible ? 1 : 0;
  const t32 = visible ? 1 : 0.975;
  const t33 = visible ? 0 : 6;
  let t34;
  if ($[34] !== visible) {
    t34 = visible ? {
      duration: 0.18,
      ease: overlayEase
    } : {
      duration: 0.12,
      ease: "easeIn"
    };
    $[34] = visible;
    $[35] = t34;
  } else {
    t34 = $[35];
  }
  let t35;
  if ($[36] !== t31 || $[37] !== t32 || $[38] !== t33 || $[39] !== t34) {
    t35 = {
      opacity: t31,
      scale: t32,
      y: t33,
      transition: t34
    };
    $[36] = t31;
    $[37] = t32;
    $[38] = t33;
    $[39] = t34;
    $[40] = t35;
  } else {
    t35 = $[40];
  }
  let t36;
  if ($[41] !== variant) {
    t36 = variant === "icon" ? <LogoLoader /> : <Spinner size="md" variant="primary" />;
    $[41] = variant;
    $[42] = t36;
  } else {
    t36 = $[42];
  }
  let t37;
  if ($[43] !== t35 || $[44] !== t36) {
    t37 = <motion.div className="pointer-events-none absolute left-1/2 z-20 -translate-x-1/2 -translate-y-1/2" style={t30} initial={false} animate={t35}>{t36}</motion.div>;
    $[43] = t35;
    $[44] = t36;
    $[45] = t37;
  } else {
    t37 = $[45];
  }
  const t38 = `
            absolute left-1/2 z-10 box-border flex w-full max-w-md -translate-x-1/2 flex-col
            items-center pb-10 text-center ${scrollNoBar}
            ${!hasVisibleMessage ? "invisible" : ""}
          `;
  let t39;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = {
      top: MESSAGE_BLOCK_TOP,
      bottom: 0
    };
    $[46] = t39;
  } else {
    t39 = $[46];
  }
  const t40 = !hasVisibleMessage;
  const T0 = LayoutGroup;
  const t41 = "loading-overlay-messages";
  const t42 = `flex w-full min-w-0 flex-col items-center ${OVERLAY_MESSAGE_STACK_GAP}`;
  let t43;
  if ($[47] !== hasPinnedLine || $[48] !== pinnedKeyDw || $[49] !== pinnedTrimmedDw) {
    t43 = hasPinnedLine ? <motion.div key={pinnedKeyDw ?? "pinned"} layout={true} initial={{
      opacity: 0,
      y: 8,
      scale: 0.99
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: -6,
      transition: {
        duration: 0.2,
        ease: overlayEase
      }
    }} transition={{
      ...feedItemTransition,
      delay: 0
    }} className="w-full min-w-0"><p className={"\n                        w-full min-w-0 wrap-break-word text-[16px] font-medium leading-snug tracking-tight\n                        whitespace-pre-line text-active-foreground antialiased drop-shadow-md\n                      "}>{pinnedTrimmedDw}</p></motion.div> : null;
    $[47] = hasPinnedLine;
    $[48] = pinnedKeyDw;
    $[49] = pinnedTrimmedDw;
    $[50] = t43;
  } else {
    t43 = $[50];
  }
  let t44;
  if ($[51] !== t43) {
    t44 = <AnimatePresence initial={false} mode="popLayout">{t43}</AnimatePresence>;
    $[51] = t43;
    $[52] = t44;
  } else {
    t44 = $[52];
  }
  const t45 = hasUnpinnedQueueRows ? <div className={`flex w-full min-w-0 flex-col items-center ${OVERLAY_MESSAGE_STACK_GAP}`}>{unpinnedQueueRowIds.map(_temp5)}</div> : hasSecondaryLines ? <div className={`flex w-full min-w-0 flex-col items-center ${OVERLAY_MESSAGE_STACK_GAP}`}><AnimatePresence initial={false} mode="popLayout">{resolvedFeedDw.map(_temp6)}</AnimatePresence></div> : null;
  let t46;
  if ($[53] !== fallbackLine || $[54] !== hasPinnedLine || $[55] !== hasSecondaryLines || $[56] !== hasUnpinnedQueueRows || $[57] !== primaryLineKeyDw) {
    t46 = !hasPinnedLine && !hasSecondaryLines && !hasUnpinnedQueueRows ? <motion.div key={primaryLineKeyDw ?? "primary"} layout={true} initial={{
      opacity: 0,
      y: 8,
      scale: 0.99
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: -6,
      transition: {
        duration: 0.2,
        ease: overlayEase
      }
    }} transition={feedItemTransition} className="w-full min-w-0"><p className={`
                        w-full min-w-0 wrap-break-word text-[16px] font-medium leading-snug tracking-tight
                        text-active-foreground antialiased drop-shadow-md
                        ${fallbackLine.includes("\n") ? "whitespace-pre-line" : "whitespace-normal"}
                      `}>{fallbackLine.length > 0 ? fallbackLine : " "}</p></motion.div> : null;
    $[53] = fallbackLine;
    $[54] = hasPinnedLine;
    $[55] = hasSecondaryLines;
    $[56] = hasUnpinnedQueueRows;
    $[57] = primaryLineKeyDw;
    $[58] = t46;
  } else {
    t46 = $[58];
  }
  let t47;
  if ($[59] !== t46) {
    t47 = <AnimatePresence initial={false} mode="popLayout">{t46}</AnimatePresence>;
    $[59] = t46;
    $[60] = t47;
  } else {
    t47 = $[60];
  }
  let t48;
  if ($[61] !== t44 || $[62] !== t45 || $[63] !== t47) {
    t48 = <div className={t42}>{t44}{t45}{t47}</div>;
    $[61] = t44;
    $[62] = t45;
    $[63] = t47;
    $[64] = t48;
  } else {
    t48 = $[64];
  }
  let t49;
  if ($[65] !== T0 || $[66] !== t48) {
    t49 = <T0 id={t41}>{t48}</T0>;
    $[65] = T0;
    $[66] = t48;
    $[67] = t49;
  } else {
    t49 = $[67];
  }
  let t50;
  if ($[68] !== liveProps || $[69] !== t38 || $[70] !== t39 || $[71] !== t40 || $[72] !== t49) {
    t50 = <div className={t38} style={t39} aria-hidden={t40} {...liveProps}>{t49}</div>;
    $[68] = liveProps;
    $[69] = t38;
    $[70] = t39;
    $[71] = t40;
    $[72] = t49;
    $[73] = t50;
  } else {
    t50 = $[73];
  }
  let t51;
  if ($[74] !== t37 || $[75] !== t50) {
    t51 = <div className={t29}>{t37}{t50}</div>;
    $[74] = t37;
    $[75] = t50;
    $[76] = t51;
  } else {
    t51 = $[76];
  }
  let t52;
  if ($[77] !== t14.div || $[78] !== t18 || $[79] !== t19 || $[80] !== t20 || $[81] !== t28 || $[82] !== t51) {
    t52 = <t14.div initial={t15} animate={t18} aria-hidden={t19} className={t20}>{t28}{t51}</t14.div>;
    $[77] = t14.div;
    $[78] = t18;
    $[79] = t19;
    $[80] = t20;
    $[81] = t28;
    $[82] = t51;
    $[83] = t52;
  } else {
    t52 = $[83];
  }
  return t52;
};
LoadingOverlay.displayName = 'LoadingOverlay';
export const GlobalLoadingOverlay = observer(() => {
  const {
    pinnedLine,
    secondaryLines,
    primaryOnly,
    secondaryFeed,
    pinnedKey,
    primaryLineKey
  } = globalStore.loadingOverlayDisplayMessages;
  return <LoadingOverlay visible={globalStore.loadingOverlay} message={primaryOnly} pinnedMessage={pinnedLine} secondaryMessages={secondaryLines} secondaryFeed={secondaryFeed} pinnedKey={pinnedKey} primaryLineKey={primaryLineKey} useDimmedBackdrop={globalStore.loadingOverlayUseDimmedBackdrop} unpinnedQueueRowIds={globalStore.unpinnedOverlayIds.slice()} />;
});
GlobalLoadingOverlay.displayName = 'GlobalLoadingOverlay';
function _temp(line) {
  return line.trim().length > 0;
}
function _temp2(item) {
  return {
    id: item.id,
    text: item.text.trim()
  };
}
function _temp3(item_0) {
  return item_0.text.length > 0;
}
function _temp4(text, index) {
  return {
    id: `fallback-${index}`,
    text
  };
}
function _temp5(rowId) {
  return <UnpinnedQueueRow key={rowId} rowId={rowId} />;
}
function _temp6(item_1) {
  return <motion.div key={item_1.id} layout={true} initial={{
    opacity: 0,
    y: 8,
    scale: 0.99
  }} animate={{
    opacity: 1,
    y: 0,
    scale: 1,
    transition: feedItemTransition
  }} exit={{
    opacity: 0,
    y: -4,
    scale: 0.99,
    transition: {
      duration: 0.2,
      ease: overlayEase
    }
  }} className="w-full min-w-0 text-center"><p className={"\n                            w-full min-w-0 wrap-break-word text-[15px] font-normal leading-snug whitespace-pre-line\n                            text-muted-foreground antialiased drop-shadow-sm\n                          "}>{item_1.text}</p></motion.div>;
}